import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import {
  ExhibitionContainer,
  Byline,
  Paragraph,
  SectionTitle,
  PageTitle,
  HeroImage,
  FeatureImage,
  HeroCaption,
  FeatureCaption,
  TextContainer,
  AudioContainer
} from '../../styles/exhibitionStyles';

export default function ExhibitionPeru() {


  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState('');

  const openLightbox = (imagePath) => {
    setCurrentImage(imagePath);
    setLightboxOpen(true);
  };


  useEffect(() => {
    document.title = "Shipibo Visions: Peru, 2024 - SLEEPR";
  }, []);

  return (
    <ExhibitionContainer>
      <HeroImage src="/img/show-peru/peru-feature.jpg" alt="London Exhibition" />
      <HeroCaption>Sleepr with visionary Shipibo shamans Maestro Eligio and Maestro Luis. Iquitos, Peru 2025</HeroCaption>
      <PageTitle>Residency In The Amazon: Peru 2025</PageTitle>
      <Byline>In 2024, Sleepr spent a month in the Amazon jungle studying visionary plant spirits with Shipibo shamans. This immersive experience led to his latest series, <em>Icaro</em>, a set of 12 new works that capture the strange and otherworldly realms of ayahuasca and DMT visions.</Byline>

      <TextContainer>

        <div onClick={() => openLightbox("/img/show-peru/peru-day-4.jpg")}>
          <FeatureImage src="/img/show-peru/peru-day-4.jpg" alt="Peru" />
        </div>
        <FeatureCaption>Sleepr in peru</FeatureCaption>

        <SectionTitle>The Amazon Diaries</SectionTitle>

        <Paragraph>
          <em>The Amazon Diaries</em> is an extensive day-by-day chronicle of Sleepr's month-long immersion in Iquitos, Peru—an epic journey that laid the foundation for his groundbreaking new series, <em>Icaro</em>. These diary entries capture the raw intensity of back-to-back plant medicine ceremonies, revealing an extraordinary, unfiltered perspective on visionary experiences. By weaving together vivid reflections and sketches recorded immediately after each ceremony, Sleepr has compiled one of the most honest accounts of a creative residency in the heart of the Amazon.</Paragraph>

          <Paragraph>
        <Link to="/articles/the-amazon-diaries-a-month-studying-plant-spirits"> You can read the full month of diary entries here (12,000 words) </Link>
        </Paragraph>

        <div onClick={() => openLightbox("/img/show-peru/peru-day-21.jpg")}>
          <FeatureImage src="/img/show-peru/peru-day-21.jpg" alt="Peru" />
        </div>

        <SectionTitle>The Birth of Icaro</SectionTitle>
        <Paragraph>In the tradition of Amazonian shamanism, the "icaros" function as a vital conduit between the spirit world of the plants and those seeking healing. Each icaro is channeled through the shaman's voice, emerging only when they tangibly sense the plant's presence guiding them in a higher dimension. Far from an improvisational chant, these songs are the product of intense study and profound dedication—shamans often undergo lengthy "dietas" in remote isolation, abstaining from normal foods and social interactions to establish a deeper bond with the plant spirits. Through this disciplined practice, a single icaro can take many months or even years to fully learn and master. When sung during ceremonies, these melodic transmissions serve as both a guiding force and a healing vibration for visitors, bringing forth the benevolent energies of the jungle. A recording from one of the shamans made during Ceremony #11 is included for your listening, offering a rare glimpse into this sacred auditory tradition.</Paragraph>

        <AudioContainer>
          <audio controls>
            <source src="/audio/icaro-recording.mp4" type="audio/mp4" />
            Your browser does not support the audio element.
          </audio> 
        </AudioContainer>

        <div onClick={() => openLightbox("/img/show-peru/peru-day-11.jpg")}>
          <FeatureImage src="/img/show-peru/peru-day-11.jpg" alt="Peru" />
        </div>

        <SectionTitle>The Songs Of The Plants</SectionTitle>
        <Paragraph>Titled <em>Icaro</em>, this forthcoming exhibition comprises twelve large-scale works, each meticulously documenting the surreal, closed-eye visuals Sleepr encountered. Inspired by the ceremonial songs of the Shipibo shamans, or "icaros," the series explores themes of plant-spirit communication, sacred geometry, and the ethereal landscapes glimpsed in DMT-induced states. These multi-layered compositions aim to bridge the gap between the hidden wonders of the Amazon and the observer, offering an intimate glimpse into otherworldly dimensions usually kept behind the veil.</Paragraph>


        <div onClick={() => openLightbox("/img/show-peru/peru-day-16.jpg")}>
          <FeatureImage src="/img/show-peru/peru-day-16.jpg" alt="Peru" />
        </div>

        <SectionTitle>A Return to Naturalist Ideals</SectionTitle>
        <Paragraph>Much like the 19th-century naturalists who ventured into uncharted territories, Sleepr adopted an immersive approach to his art, choosing to live in a remote part of the Amazon with minimal outside contact. This residency is part of his ongoing mission to experience life's peak moments firsthand—diving headlong into physically and mentally challenging adventures before committing them to visual form. By returning to these naturalist ideals, Sleepr's goal is not merely to recreate or romanticize nature, but to translate the spiritual and mysterious energies that stir beneath the jungle canopy.</Paragraph>


        <div onClick={() => openLightbox("/img/show-peru/peru-day-3.jpg")}>
          <FeatureImage src="/img/show-peru/peru-day-3.jpg" alt="Peru" />
        </div>

        <SectionTitle>Rare Residency and Peak Experiences</SectionTitle>
        <Paragraph>Spending a month deep in the jungle, Sleepr underwent twelve ayahuasca ceremonies—plus additional bufo ceremonies, wherein the venom of the Sonoran Toad is smoked for a powerful entheogenic effect. Such an undertaking is increasingly uncommon in the fast-paced modern world. Yet for Sleepr, this rare opportunity to fully immerse himself was essential: it provided a direct, unmediated perspective on the flora, fauna, and intangible presences that populate the Amazon's visionary realms.</Paragraph>

        <div onClick={() => openLightbox("/img/show-peru/peru-day-5.jpg")}>
          <FeatureImage src="/img/show-peru/peru-day-5.jpg" alt="Peru" />
        </div>


        <SectionTitle>From Diary to Artistic Imagery</SectionTitle>
        <Paragraph>Throughout the residency, Sleepr documented his nightly revelations in a video diary, capturing each facet of his thoughts, emotions, and creative sparks. He worked by lantern light and in early morning sessions, refining sketches that emerged directly from his experiences. This process involved an evolving blend of traditional drawing techniques, digital experimentation, and methodical reference to the day's notes and reflections. Each artwork represents a labor-intensive fusion of these approaches, culminating in vivid, multilayered compositions that strive to recreate the elusive landscapes he witnessed.</Paragraph>

        <div onClick={() => openLightbox("/img/show-peru/peru-day-2.jpg")}>
          <FeatureImage src="/img/show-peru/peru-day-2.jpg" alt="Peru" />
        </div>

        <SectionTitle>The Extreme Dieta</SectionTitle>
        <Paragraph>An essential precursor to this visionary work was Sleepr's 12-week "dieta"—a highly restrictive regimen practiced by certain Amazonian lineages. In preparation for the ceremonies, he drastically reduced salt, sugar, fat, oil, and abstained from pork and sexual activity. This purification process heightened his mental and physical sensitivity, enabling more profound engagements with the plants' spirits. The dedication to this practice speaks to Sleepr's commitment to authenticity, a quality reflected in the extraordinary detail and emotional resonance of his <em>Icaro</em> artworks.</Paragraph>

        <div onClick={() => openLightbox("/img/show-peru/peru-day-20.jpg")}>
          <FeatureImage src="/img/show-peru/peru-day-20.jpg" alt="Peru" />
        </div>


        <SectionTitle>Exhibition Plans and Collectors' Anticipation</SectionTitle>
        <Paragraph>Set for release in 2025, the <em>Icaro</em> series—each piece at a majestic 200 x 140 cm, giclee-printed on archival cotton rag—has already attracted significant interest, with half of the works claimed before the official preview. The original digital files are permanently secured on the blockchain, exemplifying Sleepr's fusion of tradition and innovation. When <em>Icaro</em> finally opens to the public, it will stand as both an aesthetic and metaphysical milestone, offering viewers a portal into the unseen realms that Sleepr encountered and courageously documented in the Amazon.</Paragraph>
      </TextContainer>



      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={[{ src: currentImage }]}
        carousel={{ finite: true }}
        render={{ 
          buttonPrev: () => null,
          buttonNext: () => null
        }}
      />
    </ExhibitionContainer>
  );
}
